import React, { useCallback, useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import * as Yup from 'yup';

import InitialAccess from 'app/components/ServicesForms/Registration/InitialAccess';

import { Container, Grid, makeStyles, Paper } from '@material-ui/core';
import { injectIntl } from 'react-intl';
import Breadcrumb from 'app/components/BreadCrumb';

import { useDispatch, useSelector } from 'react-redux';
import { generatePath, useHistory } from 'react-router-dom';
import notification from 'app/config/notification';
import { clearCity, clearFiles, setCity } from 'app/store/slices/forms';
import {
    clearServiceDetails,
    clearServiceDocuments,
    fetchServiceDocuments,
    getServiceDocuments,
} from 'app/store/slices/services';
import { hideLoading, setFromLogin, showLoading } from 'app/store/slices/settings';
import { clearNewRequest, clearRequestDetails, fetchNewRequest, fetchSaveRequest } from 'app/store/slices/requests';
import { preparePayload } from 'app/utils/forms/functions';
import { FormServiceContext } from 'app/utils/contexts';
import requestEndpoints from 'app/api/services/requestEndpoints';
import alertsEndpoint from 'app/api/services/alertsEndpoint';

import styles from './styles';
import { errorHandler } from 'app/utils/errorHandler';
import { SERVICES_PROPERTIES } from 'app/config/services';
import { URLS } from 'app/config/urls';
import { STATUS_CODES } from 'app/config/status';

const useStyles = makeStyles(styles);

function PortalAccess(props) {
    const classes = useStyles();
    const history = useHistory();
    const dispatch = useDispatch();

    const { intl } = props;

    const newRequest = useSelector((state) => state.requests.newRequest);
    const city = useSelector((state) => state.forms.city);
    const service = useSelector((state) => state.services.details);
    const request = useSelector((state) => state.requests.details);
    const documentsStore = useSelector((state) => state.services.documents);
    const registrationUserInfo = useSelector((state) => state.auth.registrationInfo);
    const activeCompany = useSelector((state) => state.company.activeCompany);
    const userAdmin = useSelector((state) => state.auth.userAdmin);

    const [loadingDocuments, setLoadingDocuments] = useState(false);
    const [blockDoc, setBlockDoc] = useState(false);
    const [blockPassword, setBlockPassword] = useState(false);
    const [validationSchema, setValidationSchema] = useState(
        SERVICES_PROPERTIES[service.cdServico] ? SERVICES_PROPERTIES[service.cdServico].validationSchema : {},
    );

    if (Object.keys(service).length === 0) {
        history.push(generatePath(URLS.SERVICES));
    }

    const canEdit = useMemo(() => {
        if (request.situacao) {
            return request.situacao.boEdicaoPortalExterno;
        }
        return true;
    }, [request]);

    const editing = useMemo(() => {
        return !!request.cdSolicitacao;
    }, [request.cdSolicitacao]);

    useEffect(() => {
        return () => {
            dispatch(clearCity());
            dispatch(clearFiles());
            dispatch(clearServiceDetails());
            dispatch(clearServiceDocuments());
            dispatch(clearNewRequest());
            dispatch(clearRequestDetails());
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const initialValues = useMemo(() => {
        if (editing) {
            const content = JSON.parse(request.jsonSolicitacao);
            let temp = { ...city };
            if (content.municipio) {
                temp = content.municipio;
            }
            dispatch(setCity(temp));
            if (content.documentos) {
                dispatch(
                    fetchServiceDocuments({
                        cdServico: service.cdServico,
                    }),
                );
            }
            return content;
        }

        if (Object.keys(registrationUserInfo).length > 0) {
            setBlockDoc(true);
            // setBlockEmail(true);
            setBlockPassword(true);
            const temp = { ...registrationUserInfo };
            delete temp.admin;
            delete temp.cdMunicipio;
            delete temp.boAtivo;
            delete temp.cdUsuario;
            delete temp.dtAlteracao;
            delete temp.dtCadastro;
            return temp;
        }

        if (service.cdServico && SERVICES_PROPERTIES[service.cdServico].formInitialValues)
            return SERVICES_PROPERTIES[service.cdServico].formInitialValues;

        return {};
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, editing]);

    useEffect(() => {
        const init = async () => {
            if (service.cdServico && service.cdServico !== '') {
                setLoadingDocuments(true);
                await dispatch(fetchNewRequest(true, { cdServico: service.cdServico }));
                setLoadingDocuments(false);
            }
        };
        init();
    }, [dispatch, service]);

    useEffect(() => {
        if (blockPassword) {
            const tempValidationSchema = _.cloneDeep(validationSchema);
            if (tempValidationSchema?.fields) {
                tempValidationSchema.fields.deSenha = Yup.string();
            }
            setValidationSchema(tempValidationSchema);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [blockPassword]);

    useEffect(() => {
        const init = async () => {
            if (newRequest && Object.keys(newRequest).length > 0) {
                if (newRequest.jsonSolicitacao && newRequest.jsonSolicitacao !== '') {
                    try {
                        const parsed = JSON.parse(newRequest.jsonSolicitacao);
                        dispatch(getServiceDocuments(parsed.documentos));
                    } catch (err) {
                        console.err(err);
                    }
                }
            }
        };
        init();
    }, [dispatch, newRequest]);

    const resetAndGo = useCallback(() => {
        dispatch(hideLoading());
        dispatch(setFromLogin(false));
        history.push(URLS.LOGIN);
    }, [dispatch, history]);

    const onSave = useCallback(
        async (payload) => {
            if (!canEdit) return;
            dispatch(showLoading());
            return await fetchSaveRequest({
                isFormData: true,
                preparedPayload: payload,
                dispatch,
                intl,
                showMessage: true,
                cdService: service.cdServico,
            });
        },
        [canEdit, dispatch, intl, service.cdServico],
    );

    const onSubmit = useCallback(
        async (values) => {
            if (!canEdit) return;

            const valuesCopy = _.cloneDeep(values);
            valuesCopy.municipio = city;
            delete valuesCopy.uf;

            const currentStatusCode = request.cdSituacao;

            let dataToPayload = {
                cdServico: service.cdServico,
            };

            if (request && request.cdServico) {
                dataToPayload = { ...request };
                if (request.cdSolicitacao) valuesCopy.cdSolicitacao = request.cdSolicitacao;
                if (request.situacao) valuesCopy.situacao = request.situacao;
            }

            const documentsObject = {
                store: documentsStore,
                files: values.documentos,
            };

            const preparedPayload = preparePayload({
                values: valuesCopy,
                documents: documentsObject,
                params: dataToPayload,
                activeCompany,
                userAdmin,
                registrationUserInfo,
            });

            if (preparedPayload.errors) {
                notification(intl.formatMessage({ id: 'MESSAGES.SERVICE.FORMS.MISSING_MANDATORY_INFOS' }), {
                    toastId: 'MISSING_MANDATORY_INFOS',
                    type: 'warning',
                });
                return false;
            }

            try {
                const responseSave = await onSave(preparedPayload, true);
                if (responseSave && responseSave.status === 200) {
                    if (request?.cdSolicitacao) {
                        await requestEndpoints.finishRequest(request?.cdSolicitacao);
                        if (currentStatusCode === STATUS_CODES.AGUARDANDO_RESOLUCAO_PENDENCIAS) {
                            await alertsEndpoint.deleteAlert({ cdSolicitacao: request.cdSolicitacao });
                        }
                    }
                    resetAndGo();
                }
            } catch (error) {
                errorHandler(error.response?.status, error.response?.data?.message);
            }
            dispatch(hideLoading());
        },
        [
            activeCompany,
            canEdit,
            city,
            dispatch,
            documentsStore,
            intl,
            onSave,
            registrationUserInfo,
            request,
            resetAndGo,
            service.cdServico,
            userAdmin,
        ],
    );

    const validateForm = (functionValidation) => {
        functionValidation.then((res) => {
            if (Object.keys(res).length > 0) {
                notification('O(s) campo(s) sinalizado(s) precisam de revisão.', {
                    toastId: 'VALIDATION',
                    type: 'error',
                });
            }
        });
    };

    const onCancel = () => {};

    const contextProvider = useMemo(() => {
        return {
            loadingDocuments,
            setLoadingDocuments,
            validateForm,
            onSubmit,
            onSave,
            onCancel,
            initialValues,
            validationSchema: validationSchema,
            hideActionBtn: true,
            // hideFinalizeBtn: !editing,
            editing,
            canEdit,
            blockDoc,
            blockPassword,
        };
    }, [
        blockDoc,
        blockPassword,
        canEdit,
        editing,
        initialValues,
        loadingDocuments,
        onSave,
        onSubmit,
        validationSchema,
    ]);

    const breadCrumb = [
        {
            title: intl.formatMessage({ id: 'LABELS.SERVICES.REQUEST' }),
            path: URLS.SERVICES,
        },
        {
            title: intl.formatMessage({ id: 'LABELS.COMMONS.PORTAL_ACCESS' }),
            path: '',
        },
    ];

    const rightInfo = request?.nuSolicitacaoFmt
        ? `${intl.formatMessage({ id: 'LABELS.SERVICES.REQUEST_NUMBER' })} ${request.nuSolicitacaoFmt}`
        : '';

    const handleOnBack = () => {
        if (request.nuSolicitacaoFmt && request.nuSolicitacaoFmt !== '') {
            history.push(URLS.START);
        } else {
            history.push(generatePath(URLS.SERVICES));
        }
    };

    return (
        <Container>
            <Breadcrumb onBack={() => handleOnBack()} breadCrumb={breadCrumb} rightInfo={rightInfo} />
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Paper className={classes.container} elevation={3}>
                        <FormServiceContext.Provider value={contextProvider}>
                            <InitialAccess {...props} />
                        </FormServiceContext.Provider>
                    </Paper>
                </Grid>
            </Grid>
        </Container>
    );
}

export default injectIntl(PortalAccess);
